import { AutomaticClosingPreviewDto, CaseClosingConfigurationUpdateDto } from 'types/caseClosing';
import {
    AdministratorFieldNameUpdateDto,
    ClientAssetAccountsUpdateDto,
    ConversionRateConfigurationUpdateDto,
    DebtCollectionUpdateDto,
    DefaultAdminFeePercentageUpdateDto,
    DefaultCurrencyUpdateDto,
    DefaultHandledByEmployeesUpdateDto,
    DraftRetentionDaysUpdateDto,
    RankingsRefereeRestPeriodUpdateDto,
    DraftInvoiceSupervisingPartnerNotificationUpdateDto,
    HideInvoicesOlderThanDaysUpdateDto,
    ImportationDateUpdateDto,
    InvoiceAutoGenerationUpdateDto,
    PrimaryOfficeForCasesUpdateDto,
    ProgressTrackingConfigurationDto,
    TenantConfigurationDto,
    TenantDto,
    TimecardEntryControlUpdateDto,
    TimecardIncrementUpdateDto,
} from 'types/tenants';
import { portalAPI } from './portalAPI';

// Each image query caches an object URL; revoke the previous one on refetch so re-uploads don't leak blobs.
function objectUrlHandler() {
    let last: string | null = null;
    return (response: Response) =>
        // fetchBaseQuery hands error responses to the handler too: those stay the JSON error they are,
        // and the URL the cache still serves stays alive.
        !response.ok
            ? response.text().then((text) => (text ? JSON.parse(text) : null))
            : response.blob().then((blob) => {
                  if (last) {
                      URL.revokeObjectURL(last);
                  }
                  last = URL.createObjectURL(blob);
                  return last;
              });
}
const toInvoiceLogoUrl = objectUrlHandler();
const toCurrentTenantLogoUrl = objectUrlHandler();
const toCurrentTenantIconUrl = objectUrlHandler();

const APITenantsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Tenants',
            'TenantConfiguration',
            'InvoiceLogo',
            'TenantLogo',
            'TenantIcon',
            'Invoices',
            'InvoiceMatters',
            'Matter',
            'PartyAssetFundAccounts',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCurrentTenant: builder.query<TenantDto, void>({
                query: () => '/v1/tenants/current',
                providesTags: ['Tenants'],
            }),
            getTenantConfiguration: builder.query<TenantConfigurationDto, void>({
                query: () => '/v1/tenants/configuration',
                providesTags: ['TenantConfiguration'],
            }),
            getProgressTrackingConfiguration: builder.query<ProgressTrackingConfigurationDto, void>({
                query: () => '/v1/tenants/configuration/progress-tracking',
                providesTags: ['TenantConfiguration'],
            }),
            getInvoiceLogo: builder.query<string, void>({
                query: () => ({ url: '/v1/tenants/configuration/invoice-logo', responseHandler: toInvoiceLogoUrl }),
                providesTags: ['InvoiceLogo'],
            }),
            // The uploaded invoice logo alone, which every user may read: asked for only while the tenant has one.
            getCurrentTenantLogo: builder.query<string, void>({
                query: () => ({ url: '/v1/tenants/current/logo', responseHandler: toCurrentTenantLogoUrl }),
                providesTags: ['TenantLogo'],
            }),
            // The square icon every user may read: asked for only while the tenant has one.
            getCurrentTenantIcon: builder.query<string, void>({
                query: () => ({ url: '/v1/tenants/current/icon', responseHandler: toCurrentTenantIconUrl }),
                providesTags: ['TenantIcon'],
            }),
            updateDebtCollection: builder.mutation<TenantConfigurationDto, DebtCollectionUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/debt-collection',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateTimecardEntryControl: builder.mutation<TenantConfigurationDto, TimecardEntryControlUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/timecard-entry-control',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateTimecardIncrement: builder.mutation<TenantConfigurationDto, TimecardIncrementUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/timecard-increment',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateImportationDate: builder.mutation<TenantConfigurationDto, ImportationDateUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/importation-date',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updatePrimaryOfficeForCases: builder.mutation<TenantConfigurationDto, PrimaryOfficeForCasesUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/primary-office-for-cases',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateDefaultHandledByEmployees: builder.mutation<
                TenantConfigurationDto,
                DefaultHandledByEmployeesUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/default-handled-by-employees',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateConversionRateConfiguration: builder.mutation<
                TenantConfigurationDto,
                ConversionRateConfigurationUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/conversion-rate',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateInvoiceAutoGeneration: builder.mutation<TenantConfigurationDto, InvoiceAutoGenerationUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/invoice-auto-generation',
                    method: 'PATCH',
                    body,
                }),
                // the schedule is also exposed on /v1/tenants/current, so refresh that cache too
                invalidatesTags: ['TenantConfiguration', 'Tenants'],
            }),
            updateHideInvoicesOlderThanDays: builder.mutation<
                TenantConfigurationDto,
                HideInvoicesOlderThanDaysUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/hide-invoices-older-than-days',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            // My pending invoices lists a DRAFT invoice for the case's supervising partner by this setting.
            updateDraftInvoiceSupervisingPartnerNotification: builder.mutation<
                TenantConfigurationDto,
                DraftInvoiceSupervisingPartnerNotificationUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/draft-invoice-supervising-partner-notification',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration', 'Invoices'],
            }),
            updateDraftRetentionDays: builder.mutation<TenantConfigurationDto, DraftRetentionDaysUpdateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/draft-retention-days',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateRankingsRefereeRestPeriod: builder.mutation<
                TenantConfigurationDto,
                RankingsRefereeRestPeriodUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/rankings-referee-rest-period',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            updateDefaultAdminFeePercentage: builder.mutation<
                TenantConfigurationDto,
                DefaultAdminFeePercentageUpdateDto
            >({
                query: ({ ...body }) => ({
                    url: '/v1/tenants/configuration/default-admin-fee-percentage',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration'],
            }),
            // The create forms read the default from /v1/tenants/current.
            updateDefaultCurrency: builder.mutation<TenantConfigurationDto, DefaultCurrencyUpdateDto>({
                query: (body) => ({
                    url: '/v1/tenants/configuration/default-currency',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration', 'Tenants'],
            }),
            // The forms label the field from /v1/tenants/current.
            updateAdministratorFieldName: builder.mutation<TenantConfigurationDto, AdministratorFieldNameUpdateDto>({
                query: (body) => ({
                    url: '/v1/tenants/configuration/administrator-field-name',
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['TenantConfiguration', 'Tenants'],
            }),
            // Invoices offer the close-on-send tick and the case edit form asks for a confidential reason by these
            // settings.
            updateCaseClosingConfiguration: builder.mutation<TenantConfigurationDto, CaseClosingConfigurationUpdateDto>(
                {
                    query: ({ ...body }) => ({
                        url: '/v1/tenants/configuration/case-closing',
                        method: 'PATCH',
                        body,
                    }),
                    invalidatesTags: ['TenantConfiguration', 'Invoices', 'InvoiceMatters', 'Matter'],
                }
            ),
            getAutomaticClosingPreview: builder.query<AutomaticClosingPreviewDto, { inactivityDays: number }>({
                query: ({ inactivityDays }) => ({
                    url: '/v1/automatic-case-closing/preview',
                    params: { inactivityDays },
                }),
                keepUnusedDataFor: 0,
            }),
            updateClientAssetAccounts: builder.mutation<TenantConfigurationDto, ClientAssetAccountsUpdateDto>({
                query: (body) => ({
                    url: '/v1/tenants/configuration/client-asset-accounts',
                    method: 'PATCH',
                    body,
                }),
                // The asset form offers the active accounts from their own read.
                invalidatesTags: ['TenantConfiguration', 'PartyAssetFundAccounts'],
            }),
            updateInvoiceLogo: builder.mutation<TenantConfigurationDto, File>({
                query: (file) => {
                    const body = new FormData();
                    body.append('files', file);
                    return {
                        url: '/v1/tenants/configuration/invoice-logo',
                        method: 'POST',
                        body,
                    };
                },
                // The current tenant says whether there is a custom logo, which the navigation shows.
                invalidatesTags: ['TenantConfiguration', 'InvoiceLogo', 'Tenants', 'TenantLogo'],
            }),
            resetInvoiceLogo: builder.mutation<TenantConfigurationDto, void>({
                query: () => ({
                    url: '/v1/tenants/configuration/invoice-logo',
                    method: 'DELETE',
                }),
                // Not the custom logo's read: refetched now it would answer 404. The current tenant turns it off.
                invalidatesTags: ['TenantConfiguration', 'InvoiceLogo', 'Tenants'],
            }),
            updateTenantIcon: builder.mutation<TenantConfigurationDto, File>({
                query: (file) => {
                    const body = new FormData();
                    body.append('files', file);
                    return {
                        url: '/v1/tenants/configuration/icon',
                        method: 'POST',
                        body,
                    };
                },
                invalidatesTags: ['TenantConfiguration', 'Tenants', 'TenantIcon'],
            }),
            removeTenantIcon: builder.mutation<TenantConfigurationDto, void>({
                query: () => ({
                    url: '/v1/tenants/configuration/icon',
                    method: 'DELETE',
                }),
                // As the logo reset: the icon's read would answer 404, and the current tenant turns it off.
                invalidatesTags: ['TenantConfiguration', 'Tenants'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetCurrentTenantQuery,
    useGetTenantConfigurationQuery,
    useGetProgressTrackingConfigurationQuery,
    useGetInvoiceLogoQuery,
    useGetCurrentTenantLogoQuery,
    useGetCurrentTenantIconQuery,
    useUpdateDebtCollectionMutation,
    useUpdateTimecardEntryControlMutation,
    useUpdateTimecardIncrementMutation,
    useUpdateImportationDateMutation,
    useUpdatePrimaryOfficeForCasesMutation,
    useUpdateDefaultHandledByEmployeesMutation,
    useUpdateConversionRateConfigurationMutation,
    useUpdateInvoiceAutoGenerationMutation,
    useUpdateHideInvoicesOlderThanDaysMutation,
    useUpdateDraftRetentionDaysMutation,
    useUpdateRankingsRefereeRestPeriodMutation,
    useUpdateDefaultAdminFeePercentageMutation,
    useUpdateDefaultCurrencyMutation,
    useUpdateAdministratorFieldNameMutation,
    useUpdateDraftInvoiceSupervisingPartnerNotificationMutation,
    useUpdateCaseClosingConfigurationMutation,
    useLazyGetAutomaticClosingPreviewQuery,
    useUpdateClientAssetAccountsMutation,
    useUpdateInvoiceLogoMutation,
    useResetInvoiceLogoMutation,
    useUpdateTenantIconMutation,
    useRemoveTenantIconMutation,
} = APITenantsSlice;
